import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import IconV2 from "components/V2/Icons/IconV2";
import InputV2 from "components/V2/Input/InputV2";
import useDebounce from "hooks/useDebounce";
import PagesBar from "components/V2/Pagination/PagesBar";
import Loader from "components/V2/Loader/Loader";
import { IExternalResume } from "types/ExternalResume";
import { IExternalResumesFilters } from "types/Filters/ExternalResumesFilters";
import ExternalResumesApi from "network/external/external_resumes";
import ArrowButton from "components/ArrowButton";
import formatDate from "helpers/date";

const PAGE_SIZE = 15;

const ExternalResumesOverview = () => {
  const [data, setData] = useState<IExternalResume[]>();
  const [count, setCount] = useState<number | undefined>()
  const [filters, setFilters] = useState<IExternalResumesFilters>({});
  const setFilter = <Key extends keyof IExternalResumesFilters>
    (field: Key, value: IExternalResumesFilters[Key]) => { setFilters({ ...filters, [field]: value }); }

  const debounceSetFilter = useDebounce(setFilter);

  // Fetch one page
  const fetchPage = useMemo(() =>
    async (offset: number): Promise<any[] | undefined> => {
      try {
        const response = await ExternalResumesApi.getAll({
          ...filters,
          page_size: PAGE_SIZE,
          offset
        })
        if (!response.data) throw new Error(JSON.stringify(response))
        return response.data
      } catch (e) {
        toast.error('Error while fetching page')
        console.error(e)
      }
    }, [filters]
  );

  // Fetching count is expensive do it separetly
  const fetchCount = async () => {
    try {
      const response = await ExternalResumesApi.getAll({
        ...filters,
        count: true,
        page_size: PAGE_SIZE
      })
      if (!response.data) throw new Error(JSON.stringify(response))
      setCount(response.count)
    } catch (e) {
      toast.error('Error while fetching total amount of talent')
      console.error(e)
    }
  };
  useEffect(() => { fetchCount() }, [filters]);

  return (
    <div className="w-full h-full flex flex-col px-[50px]">
      <div className="flex flex-col mt-[52px] w-full gap-5">
        <span className="text-[24px] font-poppins font-bold flex ">
          External Resumes:&nbsp;
          {count === undefined ? <Loader className='w-[30px] h-[30px]' /> : <span>{count}</span>}
        </span>
        {/* Filters */}
        {/* Search bar */}
        <InputV2
          className="w-[50%]"
          onChange={(e) => debounceSetFilter("keyword", e.target.value)}
          icon={<IconV2 iconType="SEARCH" iconClassName="w-6 h-6 translate-y-[1px]" />}
          placeholder="Search for talent by skills, role, email, name or location "
        />
      </div>

      <div className="flex flex-col flex-1 w-full overflow-auto mt-[21px] items-center pb-[100px]">
        {/* Data */}
        {/* Table */}
        <table className="table-fixed break-words w-full  border-separate border-spacing-y-6 
            font-bold font-poppins text-[14px]">
          <thead >
            <tr className="[&>th]:bg-white [&>th]:py-[20px] [&>th]:h-[72px]">
              <th className="rounded-bl-lg rounded-tl-lg">Email</th>
              <th >First name</th>
              <th>Roles</th>
              <th>Location</th>
              <th>country</th>
              <th>Years of exp.</th>
              <th>Last job</th>
              <th></th>
            </tr>
          </thead>


          <tbody >
            {(data || []).map(t => (
              <tr key={t.email} className="mt-[20px] [&>td]:bg-white [&>td]:py-[20px] [&>td]:h-[72px] [&>td]:border-none" >
                <td>{t.email}</td>
                <td>{t.first_name || "n/a"}</td>
                <td><div className="flex flex-wrap gap-2 justify-center ">
                  {t.roles?.map(r => <p key={r} className="px-2 py-1 text-xs font-medium rounded-lg bg-odf-light text-odf">
                    {r}
                  </p>) || "n/a"}
                </div></td>
                <td>{t.location || "n/a"}</td>
                <td>{t.country || "n/a"}</td>
                {/* <td><a className="text-blue2 underline" target="_blank" href={t.linkedin_url}>{t.linkedin_url}</a></td> */}
                <td>
                  <div className={`p-3 rounded-lg w-[50px] mx-auto ${yearsStyle(t.years_experience)} `}>
                    {t.years_experience || "n/a"}
                  </div>
                </td>
                <td>
                  {t.last_job_date ? formatDate.mmyyyy(t.last_job_date) : "n/a"}
                </td>
                <td className="rounded-br-lg rounded-tr-lg">
                  <a className="flex justify-center items-center" target="_blank"
                    href={`/external_resumes/details/${t.email}`}>
                    <ArrowButton onClick={() => null} direction="NEXT" />
                  </a>
                </td>
              </tr>))
            }
          </tbody>
        </table>

        {/* Loader */}
        {!data && <div className="w-full h-[100px] "><Loader /></div>}


        {/* Pages bar */}
        <PagesBar
          page_size={PAGE_SIZE}
          className="mt-[8px]"
          count={count}
          fetchPage={fetchPage}
          onLoading={() => setData(undefined)}
          onPageChange={(data) => setData(data)}
        />
      </div>

    </div>);
}



const yearsStyle = (y: number | undefined) => {
  if (!y) return "!bg-light-gray"
  if (y >= 8) return "!bg-[#DEF3DE] text-green"
  if (y >= 5) return "!bg-odf-light text-odf"
  return "!bg-orange-hue text-orange"

}



export default ExternalResumesOverview;